import React, { useState } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { trpc } from 'src/trpc';
import {
  MultiselectAutocomplete,
  type AutocompleteOption,
  type SelectedItem,
} from '@/components/ui/auto-complete';

export interface LocationSelectionItem {
  type: 'zipcode' | 'city';
  value: string; // zip code or city key
  display: string; // what to show in the badge
  zipCodes: string[]; // for cities, all associated zip codes; for individual zip codes, just that zip code
}

interface LocationSelectorProps {
  selectedItems: LocationSelectionItem[];
  onSelectionChange: (items: LocationSelectionItem[]) => void;
}

export function LocationSelector({ selectedItems, onSelectionChange }: LocationSelectorProps) {
  const [searchTerm, setSearchTerm] = useState('');

  // Cache to store all location data we've fetched
  const [locationDataCache, setLocationDataCache] = useState<{
    cities: Map<string, { city: string; state: string; zipCodes: string[] }>;
    zipCodes: Map<string, { zipCode: string; city: string; state: string }>;
  }>({
    cities: new Map(),
    zipCodes: new Map(),
  });

  // Query for coverage areas
  const { data: searchResults, isLoading } = trpc.admin.coverageArea.searchForAutocomplete.useQuery(
    { searchTerm },
    {
      enabled: searchTerm.length >= 2,
      placeholderData: keepPreviousData,
    },
  );

  // Update cache when we get new search results
  React.useEffect(() => {
    if (searchResults) {
      setLocationDataCache((prevCache) => {
        const newCache = {
          cities: new Map(prevCache.cities),
          zipCodes: new Map(prevCache.zipCodes),
        };

        // Add cities to cache
        searchResults.cities.forEach((city) => {
          const key = `${city.city}, ${city.state}`;
          newCache.cities.set(key, city);
        });

        // Add zip codes to cache
        searchResults.zipCodes.forEach((zip) => {
          newCache.zipCodes.set(zip.zipCode, zip);
        });

        return newCache;
      });
    }
  }, [searchResults]);

  // Convert search results to AutocompleteOption format
  const options: AutocompleteOption[] = React.useMemo(() => {
    if (!searchResults) return [];

    const cityOptions: AutocompleteOption[] = searchResults.cities.map((city) => ({
      value: `city:${city.city}, ${city.state}`,
      label: `${city.city}, ${city.state}`,
      description: `${city.zipCodes.length} zip codes`,
      group: 'Cities',
    }));

    const zipOptions: AutocompleteOption[] = searchResults.zipCodes.map((zipData) => ({
      value: `zip:${zipData.zipCode}`,
      label: zipData.zipCode,
      description: `${zipData.city}, ${zipData.state}`,
      group: 'Zip Codes',
    }));

    return [...cityOptions, ...zipOptions];
  }, [searchResults]);

  // Convert selected LocationSelectionItems to SelectedItems
  const selectedAutoCompleteItems: SelectedItem[] = selectedItems.map((item) => ({
    value: `${item.type === 'city' ? 'city' : 'zip'}:${item.value}`,
    label: item.display,
    description: item.type === 'city' ? `${item.zipCodes.length} zip codes` : undefined,
  }));

  const handleSelectionChange = (newSelectedItems: SelectedItem[]) => {
    const newLocationItems: LocationSelectionItem[] = [];

    newSelectedItems.forEach((selectedItem) => {
      const [type, value] = selectedItem.value.split(':', 2);

      if (type === 'city') {
        // First check cache, then current search results
        const cityData =
          locationDataCache.cities.get(value) ||
          searchResults?.cities.find((city) => `${city.city}, ${city.state}` === value);

        if (cityData) {
          const cityItem: LocationSelectionItem = {
            type: 'city',
            value: value,
            display: `${cityData.city}, ${cityData.state}`,
            zipCodes: cityData.zipCodes,
          };
          newLocationItems.push(cityItem);
        } else {
          // Preserve existing item if we don't have data in cache
          const existing = selectedItems.find(
            (item) => item.type === 'city' && item.value === value,
          );
          if (existing) {
            newLocationItems.push(existing);
          }
        }
      } else if (type === 'zip') {
        // First check cache, then current search results
        const zipData =
          locationDataCache.zipCodes.get(value) ||
          searchResults?.zipCodes.find((zip) => zip.zipCode === value);

        if (zipData) {
          const zipItem: LocationSelectionItem = {
            type: 'zipcode',
            value: zipData.zipCode,
            display: `${zipData.zipCode} (${zipData.city}, ${zipData.state})`,
            zipCodes: [zipData.zipCode],
          };
          newLocationItems.push(zipItem);
        } else {
          // Preserve existing item if we don't have data in cache
          const existing = selectedItems.find(
            (item) => item.type === 'zipcode' && item.value === value,
          );
          if (existing) {
            newLocationItems.push(existing);
          }
        }
      }
    });

    // Apply conflict resolution logic
    const resolvedItems = resolveLocationConflicts(newLocationItems);
    onSelectionChange(resolvedItems);
  };

  // Helper function to resolve conflicts between cities and zip codes
  const resolveLocationConflicts = (items: LocationSelectionItem[]): LocationSelectionItem[] => {
    const result: LocationSelectionItem[] = [];

    // First, add all cities
    const cities = items.filter((item) => item.type === 'city');
    result.push(...cities);

    // Then, add zip codes that are not already covered by cities
    const zipCodes = items.filter((item) => item.type === 'zipcode');
    const cityZipCodes = cities.flatMap((city) => city.zipCodes);

    zipCodes.forEach((zipItem) => {
      if (!cityZipCodes.includes(zipItem.zipCodes[0])) {
        result.push(zipItem);
      }
    });

    return result;
  };

  return (
    <MultiselectAutocomplete
      options={options}
      selectedItems={selectedAutoCompleteItems}
      onSelectionChange={handleSelectionChange}
      placeholder="Select zip codes or cities"
      emptyMessage="No locations found."
      searchPlaceholder="Search zip codes or cities..."
      isLoading={isLoading}
      onSearchChange={setSearchTerm}
      minSearchLength={2}
    />
  );
}
